/**
 * 持久化令牌存储。
 *
 * ⚠ 为什么令牌只能由 JS 持有：后端 `allow-credentials: false`，令牌走 `Authorization` 头，
 *   **不用 Cookie**(契约 §8.3 第 3 条)。所以「放 httpOnly cookie 防 XSS」这条路是关着的 ——
 *   代价要认：一旦有 XSS，令牌就能被读走。缓解只能靠短时效(access 5 分钟)与登出吊销 refresh。
 *
 * ⚠ M3 接 Capacitor 壳时可能换成 Preferences 插件(iOS 上 WKWebView 的 localStorage
 *   在磁盘紧张时会被系统清掉)。换的时候只改本文件 —— 这就是它是一个接口的原因。
 */
import { createMemoryTokenStore, type TokenStore, type Tokens } from '@cione/api-client'

const STORAGE_KEY = 'cione.tokens.v1'

interface StoredShape {
  accessToken?: unknown
  refreshToken?: unknown
  expiresAt?: unknown
}

function readFrom(storage: Storage): Tokens | null {
  const raw = storage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredShape
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.refreshToken !== 'string' ||
      typeof parsed.expiresAt !== 'number'
    ) {
      // 形状不对(升级过、被人手改过)：当没有，而不是崩在启动路径上
      storage.removeItem(STORAGE_KEY)
      return null
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      expiresAt: parsed.expiresAt,
    }
  } catch {
    storage.removeItem(STORAGE_KEY)
    return null
  }
}

/**
 * 落 localStorage 的令牌存储。
 *
 * ⚠ 隐身模式、或壳里存储被禁时 `localStorage` 会抛 —— 那时退回内存版：
 *   用户这一程还能用，只是重开要再领一次匿名令牌。
 */
export function createPersistentTokenStore(storage: Storage | null = safeStorage()): TokenStore {
  if (!storage) return createMemoryTokenStore()

  const inner = createMemoryTokenStore(readFrom(storage))

  /*
   * ⚠⚠ 跨标签页同步(W1-3)。别的标签刷新后写了新令牌，本标签必须跟上 ——
   *   不跟的话，本标签内存里留着的是那张**已经被消费掉**的 refresh
   *   (后端 `RefreshTokenStore` 一次性，用掉即删键)，拿它去刷必然失败，
   *   用户会在「我明明刚在另一个标签里聊着」的时候被踢去登录。
   *   ⚠ 这只是一半，另一半是 api-client 的跨标签刷新锁(cross-tab-lock.ts)。
   *
   * ⚠ `storage` 事件只在**其他**标签触发 —— 本标签自己 `set` 收不到自己的事件，
   *   所以这里既不会和下面的 `set` 打架，也不存在回写循环。
   * ⚠ `event.key === null` 是 `storage.clear()`：当作令牌被清掉处理。
   * ⚠ 监听不摘：这个 store 与页面同生命周期。
   */
  globalThis.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return
    inner.set(readFrom(storage))
  })

  return {
    // 包一层箭头函数而不是直接传方法引用：直接传会把 this 丢掉(lint 也会拦)
    get: () => inner.get(),
    set(tokens) {
      try {
        if (tokens) storage.setItem(STORAGE_KEY, JSON.stringify(tokens))
        else storage.removeItem(STORAGE_KEY)
      } catch {
        // 写不进去不影响本次会话，别把它变成一个用户能看见的错误
      }
      inner.set(tokens)
    },
    subscribe: (listener) => inner.subscribe(listener),
  }
}

function safeStorage(): Storage | null {
  try {
    const probe = '__cione_probe__'
    globalThis.localStorage.setItem(probe, '1')
    globalThis.localStorage.removeItem(probe)
    return globalThis.localStorage
  } catch {
    return null
  }
}
